import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import type { AuthUser, EffectiveAccess } from '@shared/api';
import { HttpError, http, saveToken } from '@/services/http';

interface LoginResponse {
  token: string;
  user: { id: number; username: string; role: AuthUser['role']; display_name?: string; provider?: 'local' | 'feishu'; access?: EffectiveAccess };
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null);
  const loading = ref(false);
  const initialized = ref(false);
  const error = ref('');
  /** 初始化时后端不可达（重启中/网络故障）的原因；非空时页面应显示"重试"而不是登录页 */
  const initError = ref('');

  const isAuthenticated = computed(() => Boolean(user.value));
  const legacyLevel = computed(
    () => ({ owner: 100, super_admin: 90, admin: 80, dept_admin: 50, custom: 50, editor: 20, viewer: 10 })[user.value?.role || 'viewer'],
  );
  const accessLevel = computed(() => user.value?.access?.level ?? legacyLevel.value ?? 0);
  const permissions = computed(() => new Set(user.value?.access?.permissions || []));

  function isLegacyAdminPermission(code: string): boolean {
    return code === 'menu.permissions'
      || code === 'menu.users'
      || code === 'menu.audit'
      || code.startsWith('function.permissions.')
      || code.startsWith('function.users.')
      || code.startsWith('function.audit.')
      || code.startsWith('button.permissions.')
      || code.startsWith('button.users.');
  }

  function can(code: string): boolean {
    if (user.value?.access) return permissions.value.has(code);
    if (accessLevel.value >= 80) return true;
    if (isLegacyAdminPermission(code)) return false;
    if (code.startsWith('menu.') || code.startsWith('function.') || code.startsWith('field.')) return true;
    if (accessLevel.value >= 20) return code.startsWith('button.');
    return code.endsWith('.export') || code.endsWith('.run');
  }

  function canField(module: string, key: string): boolean {
    const catalog = user.value?.access?.field_catalog?.[module];
    if (!catalog?.includes(key)) return true;
    return Boolean(user.value?.access?.field_access?.[module]?.includes(key));
  }

  const canEdit = computed(() =>
    (accessLevel.value >= 20
      && [...permissions.value].some((code) => /^button\..+\.(edit|create|batch_edit|import|settings)$/.test(code)))
    || (!user.value?.access && accessLevel.value >= 20),
  );
  const isAdmin = computed(() => accessLevel.value >= 80);

  async function restore(): Promise<void> {
    loading.value = true;
    initError.value = '';
    try {
      const result = await http.get<{ user: AuthUser }>('/api/auth/me');
      user.value = result.user;
    } catch (cause) {
      // 只有「明确的 401」才算未登录/令牌过期 → 清 token 回登录页。
      // ⚠️ 其它错误（网络抖动、服务重启中的 502、反向代理超时）绝不能清 token、也不能当成未登录：
      //    否则用户刷新页面时明明持有有效令牌也会被登出并弹去飞书（2026-09-16 排查出的掉登录主因）。
      const unauthorized = cause instanceof HttpError && cause.status === 401;
      if (unauthorized) {
        user.value = null;
        saveToken(null);
      } else {
        // 瞬时故障：等 1.5s 重试一次（服务重启一般几十秒内恢复）
        await new Promise((resolve) => setTimeout(resolve, 1500));
        try {
          const retry = await http.get<{ user: AuthUser }>('/api/auth/me');
          user.value = retry.user;
        } catch (retryCause) {
          // 仍失败：保留 token，提示"连不上服务器"并给重试入口（不要显示登录页把人赶去飞书）
          user.value = null;
          const status = retryCause instanceof HttpError ? retryCause.status : 0;
          initError.value = status >= 500 ? '服务器暂时不可用（可能正在重启）' : '网络连接异常，请检查网络后重试';
        }
      }
    } finally {
      loading.value = false;
      initialized.value = true;
    }
  }

  async function login(username: string, password: string): Promise<void> {
    loading.value = true;
    error.value = '';
    try {
      const result = await http.post<LoginResponse>('/api/auth/login', { username, password });
      saveToken(result.token);
      user.value = { uid: result.user.id, ...result.user };
    } catch (cause) {
      error.value = cause instanceof Error ? cause.message : '登录失败';
      throw cause;
    } finally {
      loading.value = false;
      initialized.value = true;
    }
  }

  async function logout(): Promise<void> {
    try {
      await http.post('/api/auth/logout');
    } finally {
      saveToken(null);
      user.value = null;
    }
  }

  return { user, loading, initialized, error, initError, isAuthenticated, accessLevel, permissions, can, canField, canEdit, isAdmin, restore, login, logout };
});
